import { getAiSettings, setAiSettings, providerMeta, isApibooDefaultMigrated, markApibooDefaultMigrated } from './aiSettings';
import { hasAiKey, vaultKeyState } from './keyStore';

/**
 * One-time default migration to the Apiboo assistant (22.08.2026).
 *
 * `DEFAULT_PROVIDER` became `apiboo` for fresh installs, but every install
 * that opened the panel before that has `{provider:'anthropic', …}` (the old
 * default) in localStorage — so the picker showed Claude and a turn only
 * reached Apiboo by fallback. Owner: "Apiboo selected by default if not
 * changed to another model".
 *
 * "Changed" is read as: a key saved for the stored BYO provider. So:
 *   - stored provider is keyless (or nothing stored)  → no-op, flag set
 *   - stored BYO provider WITH a key in the vault      → untouched, flag set
 *   - stored BYO provider WITHOUT a key                → switched to apiboo,
 *                                                        AI_SETTINGS_CHANGED_EVENT, flag set
 *   - vault locked / unavailable / cannot answer      → nothing this time, NO
 *                                                        flag: the next readiness
 *                                                        check tries again
 *
 * The flag lives inside `localStorage.aiChatSettings` (aiSettings.js) so it
 * never re-runs once decided — a user who later picks a BYO provider without
 * a key on purpose keeps that choice.
 *
 * Never throws; called from resolveAiTarget() before every resolution.
 * Resolves true only when it actually switched.
 */
export async function migrateDefaultToApibooOnce() {
  try {
    if (isApibooDefaultMigrated()) return false;
    const { provider } = getAiSettings();
    if (providerMeta(provider).keyless) {
      markApibooDefaultMigrated();
      return false;
    }
    if ((await vaultKeyState()) !== 'unlocked') return false;
    let keyed;
    try {
      keyed = await hasAiKey(provider);
    } catch {
      return false;
    }
    // Re-check after the awaits: a Settings pick / key save during
    // vault_list may have marked the migration done — a deliberate user
    // action always wins over this one-time default (review N7, 22.08.2026).
    if (isApibooDefaultMigrated()) return false;
    // Decide before writing the preference: setAiSettings preserves the flag.
    markApibooDefaultMigrated();
    if (keyed) return false;
    setAiSettings({ provider: 'apiboo', model: 'apiboo' });
    return true;
  } catch {
    return false;
  }
}
